import { Injectable } from '@angular/core';

import { FixtureViewType } from '@cds/betting-offer';
import {
    EventModel,
    EventScoreboard,
    Message,
    PairGameScore,
    PeriodGameScore,
    ScoreboardType,
    SetGameScore,
    TeamType,
} from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import {
    AmericanFootballPeriodInfo,
    AmericanFootballQuarters,
    FootballPeriodInfo,
    IceHockeyPeriodInfo,
    IceHockeyPeriods,
    SportConstant,
} from '@frontend/sports/common/core/data-access/constants';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { differenceBy, isEmpty, padStart } from 'lodash-es';
import { Observable, Subject } from 'rxjs';

import {
    AmericanFootballMessageIds,
    BaseballMessageIds,
    BasketBallPeriodIds,
    BasketballMessageIds,
    CustomMessage,
    FootballMessageIds,
    IceHockeyMessageIds,
    LiveAlertEvent,
    LiveAlertMessage,
    LiveAlertMessageType,
    TouchDownInfo,
} from '../showcase.model';

@Injectable()
export class LiveAlertManager {
    queuedAlerts: LiveAlertMessage[] = [];
    alerts: LiveAlertMessage[] = [];
    private newAlertSubject = new Subject<LiveAlertMessage>();
    private emptyAlertsSubject = new Subject<Array<LiveAlertMessage>>();
    private endOfQuarterOrPeriodId = 1;
    private viewType: string;
    private matchType: number;

    constructor(private sitecore: Sitecore) {}

    private triggerNotification(newAlert: LiveAlertMessage): void {
        this.queuedAlerts.push(newAlert);
        this.newAlertSubject.next(newAlert);
    }

    get newAlert(): Observable<LiveAlertMessage> {
        return this.newAlertSubject.asObservable();
    }

    get alertsEmptied(): Observable<Array<LiveAlertMessage>> {
        return this.emptyAlertsSubject.asObservable();
    }

    private parseMessage(message: Message, updatedEvent: LiveAlertEvent, oldEvent: LiveAlertEvent, teamName: string): LiveAlertMessage | undefined {
        const eventId = updatedEvent.eventId;
        const sportId = updatedEvent.sportId;
        const eventTime = updatedEvent.time || updatedEvent.period.name;
        const time = message.type === CustomMessage.CustomMessageType || !message.time ? `(${eventTime || ''})` : `(${message.time})`;

        if (!message) {
            return;
        }

        if (this.isDuplicateAlert(eventId, message)) {
            return;
        }

        const isTeam1Message = message.teamType === TeamType.Team01;
        const isTeam2Message = message.teamType === TeamType.Team02;

        let parsedAlert: LiveAlertMessage | undefined = {
            eventId,
            sportId,
            team: teamName,
            event: '',
            order: message.order,
            team1Score: updatedEvent.participant1Score,
            team2Score: updatedEvent.participant2Score,
            text: message.text,
        };
        switch (sportId) {
            case SportConstant.Soccer:
                parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, false, false, false, time);
                switch (message.type) {
                    case FootballMessageIds.Goal:
                        if (updatedEvent.period && updatedEvent.period.id === FootballPeriodInfo.PenaltyShootout) {
                            parsedAlert = undefined;
                            break;
                        }
                        parsedAlert.event = this.sitecore.showcaseMessages.Goal;
                        parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, isTeam1Message, isTeam2Message, true, time);
                        break;
                    case FootballMessageIds.Penalty:
                        parsedAlert.event = this.sitecore.showcaseMessages.Penalty;
                        break;
                    case FootballMessageIds.PenaltyScored:
                        parsedAlert.event = this.sitecore.showcaseMessages.PenaltyScored;
                        break;
                    case FootballMessageIds.PenaltyMissed:
                        parsedAlert.event = this.sitecore.showcaseMessages.PenaltyMissed;
                        break;
                    case FootballMessageIds.RedCard:
                        parsedAlert.event = this.sitecore.showcaseMessages.RedCard;
                        break;
                    case FootballMessageIds.Custom:
                        this.handleCustomMessages(updatedEvent, oldEvent, parsedAlert, message);
                        break;
                    default:
                        parsedAlert = undefined;
                        break;
                }
                break;
            case SportConstant.Basketball:
                switch (message.type) {
                    case BasketballMessageIds.Messages:
                        switch (updatedEvent.period.id) {
                            case BasketBallPeriodIds.FirstQuarterIntermission:
                                parsedAlert.event =
                                    this.matchType === 2 ? this.sitecore.showcaseMessages.EndOfQ1 : this.sitecore.showcaseMessages.EndOf1H;
                                break;

                            case BasketBallPeriodIds.SecondQuarterIntermission:
                                parsedAlert.event =
                                    this.matchType === 2 ? this.sitecore.showcaseMessages.EndOfQ2 : this.sitecore.showcaseMessages.EndOf2H;
                                break;

                            case BasketBallPeriodIds.ThirdQuarterIntermission:
                                parsedAlert.event = this.sitecore.showcaseMessages.EndOfQ3;
                                break;

                            case BasketBallPeriodIds.FourthQuarterIntermission:
                                parsedAlert.event = this.sitecore.showcaseMessages.EndOfQ4;
                                break;

                            case BasketBallPeriodIds.Overtime:
                                parsedAlert.event = this.sitecore.showcaseMessages.OTStart;
                                break;

                            case BasketBallPeriodIds.End:
                                parsedAlert.event = this.sitecore.showcaseMessages.MatchFinished;
                                break;
                            default:
                                parsedAlert = undefined;
                                break;
                        }
                        break;
                    default:
                        parsedAlert = undefined;
                        break;
                }
                break;
            case SportConstant.AmericanFootball:
                switch (message.type) {
                    case AmericanFootballMessageIds.TouchDown:
                        parsedAlert.event = this.sitecore.showcaseMessages.TouchDown;
                        parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, isTeam1Message, isTeam2Message, true, time);
                        break;
                    case AmericanFootballMessageIds.TwoPointConversion:
                        parsedAlert.event = this.sitecore.showcaseMessages.PtConversion.replace('{val}', '2');
                        parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, isTeam1Message, isTeam2Message, true, time);
                        break;
                    case AmericanFootballMessageIds.OnePointConversion:
                        parsedAlert.event = this.sitecore.showcaseMessages.PtConversion.replace('{val}', '1');
                        parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, isTeam1Message, isTeam2Message, true, time);
                        break;
                    case AmericanFootballMessageIds.Safety:
                        parsedAlert.event = this.sitecore.showcaseMessages.Safety;
                        parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, isTeam1Message, isTeam2Message, true, time);
                        break;
                    case AmericanFootballMessageIds.FieldGoal:
                        parsedAlert.event = this.sitecore.showcaseMessages.FieldGoal;
                        parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, isTeam1Message, isTeam2Message, true, time);
                        break;
                    case AmericanFootballMessageIds.Quarter:
                        switch (updatedEvent.period.id) {
                            case AmericanFootballPeriodInfo.IntermissionQ1:
                                parsedAlert.event = this.sitecore.showcaseMessages.EndOfQ1;
                                break;
                            case AmericanFootballPeriodInfo.IntermissionQ2:
                                parsedAlert.event = this.sitecore.showcaseMessages.EndOfQ2;
                                break;
                            case AmericanFootballPeriodInfo.IntermissionQ3:
                                parsedAlert.event = this.sitecore.showcaseMessages.EndOfQ3;
                                break;
                            case AmericanFootballQuarters.Overtime:
                                parsedAlert.event = this.sitecore.showcaseMessages.OTStart;
                                break;
                            case AmericanFootballPeriodInfo.Finished:
                                parsedAlert.event = this.sitecore.showcaseMessages.MatchFinished;
                                break;
                            default:
                                parsedAlert = undefined;
                                break;
                        }
                        break;
                    case AmericanFootballMessageIds.Custom:
                        const isAnyCancelledTouchDown =
                            this.isCancelledTouchDown(oldEvent.participant1Score, updatedEvent.participant1Score) ||
                            this.isCancelledTouchDown(oldEvent.participant2Score, updatedEvent.participant2Score);

                        if (isAnyCancelledTouchDown) {
                            this.handleCustomMessages(updatedEvent, oldEvent, parsedAlert, message, isAnyCancelledTouchDown);
                        } else {
                            parsedAlert = undefined;
                        }
                        break;
                    default:
                        parsedAlert = undefined;
                        break;
                }
                break;
            case SportConstant.Icehockey:
                switch (message.type) {
                    case IceHockeyMessageIds.Goal:
                        if (updatedEvent.period && updatedEvent.period.id === IceHockeyPeriodInfo.PenaltyShootout) {
                            parsedAlert = undefined;
                            break;
                        }
                        parsedAlert.event = this.sitecore.showcaseMessages.Goal;
                        parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, isTeam1Message, isTeam2Message, true, time);
                        break;
                    case IceHockeyMessageIds.PenaltyScored:
                        parsedAlert.event = this.sitecore.showcaseMessages.PenaltyScored;
                        break;
                    case IceHockeyMessageIds.PenaltyMissed:
                        parsedAlert.event = this.sitecore.showcaseMessages.PenaltyMissed;
                        break;
                    case IceHockeyMessageIds.Messages:
                        switch (updatedEvent.period.id) {
                            case IceHockeyPeriodInfo.IntermissionPeriod1:
                                parsedAlert.event = this.sitecore.showcaseMessages.EndOfP1;
                                break;
                            case IceHockeyPeriodInfo.IntermissionPeriod2:
                                parsedAlert.event = this.sitecore.showcaseMessages.EndOfP2;
                                break;
                            case IceHockeyPeriodInfo.RegularTimeOver:
                                parsedAlert.event = this.sitecore.showcaseMessages.EndOfP3;
                                break;
                            case IceHockeyPeriods.Overtime:
                                parsedAlert.event = this.sitecore.showcaseMessages.OTStart;
                                break;
                            case IceHockeyPeriodInfo.Finished:
                                parsedAlert.event = this.sitecore.showcaseMessages.MatchFinished;
                                break;
                            default:
                                parsedAlert = undefined;
                                break;
                        }
                        break;
                    case IceHockeyMessageIds.Custom:
                        this.handleCustomMessages(updatedEvent, oldEvent, parsedAlert, message);
                        break;
                    default:
                        parsedAlert = undefined;
                        break;
                }
                break;
            case SportConstant.Baseball:
                switch (message.type) {
                    case BaseballMessageIds.Custom:
                        if (!updatedEvent || !oldEvent) {
                            parsedAlert = undefined;
                        } else {
                            if (updatedEvent.currentlyServing !== oldEvent.currentlyServing) {
                                parsedAlert.team = updatedEvent.currentlyServing === 2 ? updatedEvent.team2Name : updatedEvent.team1Name;
                                parsedAlert.event = this.sitecore.showcaseMessages.BattingSwitch;
                                parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, false, false, false);
                            } else if (
                                updatedEvent.participant1Score !== oldEvent.participant1Score ||
                                updatedEvent.participant2Score !== oldEvent.participant2Score
                            ) {
                                parsedAlert.event = this.sitecore.showcaseMessages.RunsScored;
                                if (oldEvent.didScore(updatedEvent, 1)) {
                                    parsedAlert.team = updatedEvent.team1Name;
                                    parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, true, false, true);
                                } else if (oldEvent.didScore(updatedEvent, 2)) {
                                    parsedAlert.team = updatedEvent.team2Name;
                                    parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, false, true, true);
                                } else {
                                    parsedAlert = undefined;
                                }
                            } else {
                                parsedAlert = undefined;
                            }
                        }
                        break;
                    default:
                        parsedAlert = undefined;
                        break;
                }
                break;
            default:
                parsedAlert = undefined;
                break;
        }

        if (parsedAlert && (parsedAlert.team || message.type === this.endOfQuarterOrPeriodId)) {
            if (message.type === this.endOfQuarterOrPeriodId) {
                parsedAlert = this.getParsedAlert(updatedEvent, parsedAlert);
            }
            this.triggerNotification(parsedAlert);
        }

        return;
    }

    private getParsedAlert(updatedEvent: LiveAlertEvent, parsedAlert: LiveAlertMessage): LiveAlertMessage {
        let subMessages: LiveAlertMessageType[] = [];
        if (
            updatedEvent.participant1Score !== undefined &&
            updatedEvent.participant2Score !== undefined &&
            updatedEvent.participant1Score !== updatedEvent.participant2Score
        ) {
            const upScore = Math.abs(updatedEvent.participant1Score - updatedEvent.participant2Score);
            const upScoreTeam = updatedEvent.participant1Score > updatedEvent.participant2Score ? updatedEvent.team1Name : updatedEvent.team2Name;
            parsedAlert.team = upScoreTeam;
            const isMatchDecided = this.checkMatchDecided(updatedEvent);
            const resultMessage = isMatchDecided ? this.sitecore.showcaseMessages.WinBy : this.sitecore.showcaseMessages.UpBy;
            subMessages = [{ message: `${resultMessage} ${upScore}`, isHighlighted: false }];
            parsedAlert.messageType = subMessages;
        } else {
            parsedAlert.team = this.sitecore.showcaseMessages.Tie;
            const isEuropeanFixture = this.viewType === FixtureViewType.European;

            subMessages = isEuropeanFixture
                ? [{ message: `${updatedEvent.team1Name} - ${updatedEvent.team2Name}`, isHighlighted: false }]
                : [{ message: `${updatedEvent.team1Name} @ ${updatedEvent.team2Name}`, isHighlighted: false }];
            parsedAlert.messageType = subMessages;
        }

        return parsedAlert;
    }

    private checkMatchDecided(updatedEvent: LiveAlertEvent): boolean {
        return (
            ((updatedEvent.period.id === BasketBallPeriodIds.RegularTimeOver || updatedEvent.period.id === BasketBallPeriodIds.End) &&
                updatedEvent.sportId === SportConstant.Basketball) ||
            ((updatedEvent.period.id === IceHockeyPeriodInfo.RegularTimeOver || updatedEvent.period.id === IceHockeyPeriodInfo.Finished) &&
                updatedEvent.sportId === SportConstant.Icehockey) ||
            ((updatedEvent.period.id === AmericanFootballPeriodInfo.RegularTimeOver ||
                updatedEvent.period.id === AmericanFootballPeriodInfo.Finished) &&
                updatedEvent.sportId === SportConstant.AmericanFootball)
        );
    }

    private handleCustomMessages(
        updatedEvent: LiveAlertEvent,
        oldEvent: LiveAlertEvent,
        parsedAlert: LiveAlertMessage | undefined,
        message: Message,
        isCancelledTouchDown?: boolean,
    ): void {
        const eventTime = updatedEvent.time || updatedEvent.period.name;
        const time = message.type === CustomMessage.CustomMessageType || !message.time ? `(${eventTime || ''})` : `(${message.time})`;
        if (updatedEvent && oldEvent && parsedAlert) {
            parsedAlert.event = isCancelledTouchDown
                ? this.sitecore.showcaseMessages.CancelledTouchDown
                : this.sitecore.showcaseMessages.CancelledGoal;
            if (oldEvent.didRevertScore(updatedEvent, 1)) {
                parsedAlert.team = updatedEvent.team1Name;
                parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, true, false, true, time);
            } else if (oldEvent.didRevertScore(updatedEvent, 2)) {
                parsedAlert.team = updatedEvent.team2Name;
                parsedAlert.messageType = this.getLiveAlertMessageTypes(updatedEvent, false, true, true, time);
            } else {
                parsedAlert = undefined;
            }
        }
    }

    private getLiveAlertMessageTypes(
        updatedEvent: LiveAlertEvent,
        team1Highlight: boolean,
        team2Highlight: boolean,
        withParticipantScore?: boolean,
        time?: string,
    ): LiveAlertMessageType[] {
        let subMessages: LiveAlertMessageType[] = [];
        const timeValue = time || '';
        switch (updatedEvent.sportId) {
            case SportConstant.Baseball:
                subMessages = [{ message: updatedEvent.period.name, isHighlighted: false }];
                if (withParticipantScore) {
                    subMessages = [
                        { message: `${updatedEvent.participant1Score}`, isHighlighted: team1Highlight },
                        { message: `- ${updatedEvent.participant2Score}`, isHighlighted: team2Highlight },
                        { message: updatedEvent.period.name, isHighlighted: false },
                    ];
                }
                break;
            case SportConstant.Soccer:
            case SportConstant.AmericanFootball:
            case SportConstant.Icehockey:
                subMessages = [{ message: timeValue, isHighlighted: false }];
                if (withParticipantScore) {
                    subMessages = [
                        { message: `${updatedEvent.participant1Score}`, isHighlighted: team1Highlight },
                        { message: `- ${updatedEvent.participant2Score}`, isHighlighted: team2Highlight },
                        { message: timeValue, isHighlighted: false },
                    ];
                }
                break;
            default:
                break;
        }

        return subMessages;
    }

    private getLatestMessages(updatedEvent: EventModel, oldEvent: EventModel): Message[] | undefined {
        let messages: Message[] | undefined;
        if (
            oldEvent.scoreboard.messages &&
            updatedEvent.scoreboard.messages &&
            updatedEvent.scoreboard.messages.length > 0 &&
            oldEvent.scoreboard.messages.length < updatedEvent.scoreboard.messages.length
        ) {
            messages = differenceBy(updatedEvent.scoreboard.messages, oldEvent.scoreboard.messages, (m) => m.order);
        }

        return messages;
    }

    private checkChangesInScoreOrTurn(messages: Message[] | undefined, latestLiveEvent: LiveAlertEvent, oldLiveEvent: LiveAlertEvent): boolean {
        return (
            (isEmpty(messages) || latestLiveEvent.sportId === SportConstant.Baseball) &&
            (latestLiveEvent.participant1Score !== oldLiveEvent.participant1Score ||
                latestLiveEvent.participant2Score !== oldLiveEvent.participant2Score ||
                (latestLiveEvent.currentlyServing !== undefined &&
                    oldLiveEvent.currentlyServing !== undefined &&
                    latestLiveEvent.currentlyServing !== oldLiveEvent.currentlyServing))
        );
    }

    add(updatedEvent: EventModel, oldEvent: EventModel): void {
        let messages = this.getLatestMessages(updatedEvent, oldEvent);
        this.viewType = updatedEvent.viewType;
        if (updatedEvent.sport.id === SportConstant.Basketball) {
            this.matchType = (updatedEvent.scoreboard.score as PeriodGameScore).matchType;
        }

        const updatedEventLatestAlert = this.getParticipantScores(updatedEvent);
        const oldEventLatestAlert = this.getParticipantScores(oldEvent);

        if (
            !updatedEventLatestAlert ||
            !oldEventLatestAlert ||
            Boolean(
                updatedEventLatestAlert.period &&
                    (updatedEventLatestAlert.period.id === 255 || updatedEventLatestAlert.period.id === 256) &&
                    updatedEvent.scoreboard.type !== ScoreboardType.PeriodGame,
            )
        ) {
            return;
        }

        if (this.checkChangesInScoreOrTurn(messages, updatedEventLatestAlert, oldEventLatestAlert)) {
            const messageOrder = Math.floor(Math.random() * 0x1000);
            messages = [
                {
                    type: CustomMessage.CustomMessageType,
                    order: messageOrder,
                    time: '',
                    text: 'custom' + messageOrder,
                    icon: null,
                },
            ]; // creating custom message
        }

        messages &&
            messages.forEach((message) => {
                const teamName = this.getTeamName(message, updatedEvent);
                this.parseMessage(message, updatedEventLatestAlert, oldEventLatestAlert, teamName);
            });
    }

    emptyList(): void {
        this.queuedAlerts = [];
        this.alerts = [];
        this.emptyAlertsSubject.next(this.alerts);
    }

    private getTeamName(message: Message, updatedEvent: EventModel): string {
        switch (message.teamType) {
            case TeamType.Team01:
            case TeamType.Team02:
                return updatedEvent.participants[message.teamType].shortName || updatedEvent.participants[message.teamType].name || '';
            default:
                return '';
        }
    }

    private getTime(scoreboard: EventScoreboard): string {
        if (!scoreboard.timer) {
            return '';
        }
        const base = new Date(scoreboard.timer.base).getTime();
        const now = DateProviderService.now();

        const timeSpan = scoreboard.timer.seconds + Math.round((now - base) / 1000);

        const minutes = Math.floor(timeSpan / 60);
        const seconds = timeSpan % 60;

        return `${this.pad(minutes)}:${this.pad(seconds)}`;
    }

    private pad(val: number): string {
        return padStart(val.toString(), 2, '0');
    }

    private getParticipantScores(event: EventModel): LiveAlertEvent | undefined {
        let liveAlert: LiveAlertEvent | undefined;
        const period = event.scoreboard.period ? event.scoreboard.period : { id: -1, name: '' };
        if (event.scoreboard.started) {
            switch (event.scoreboard.type) {
                case ScoreboardType.PeriodGame:
                    const periodGameScore = event.scoreboard.getScore<PeriodGameScore>();
                    if (periodGameScore) {
                        liveAlert = new LiveAlertEvent(
                            periodGameScore.participant1.total,
                            periodGameScore.participant2.total,
                            event.id,
                            event.sport.id,
                            event.participants,
                            period,
                            event.scoreboard.time,
                            periodGameScore.turn,
                        );
                    }
                    break;
                case ScoreboardType.PairGame:
                    const pairGameScore = event.scoreboard.getScore<PairGameScore>();
                    if (pairGameScore) {
                        liveAlert = new LiveAlertEvent(
                            pairGameScore.participant1,
                            pairGameScore.participant2,
                            event.id,
                            event.sport.id,
                            event.participants,
                            period,
                            this.getTime(event.scoreboard),
                            pairGameScore.currentlyServing,
                        );
                    }
                    break;
                case ScoreboardType.SetGame:
                    const setGameScore = event.scoreboard.getScore<SetGameScore>();
                    if (setGameScore) {
                        liveAlert = new LiveAlertEvent(
                            setGameScore.participant1WonSets,
                            setGameScore.participant2WonSets,
                            event.id,
                            event.sport.id,
                            event.participants,
                            period,
                            event.scoreboard.time,
                            setGameScore.currentlyServing,
                        );
                    }
                    break;
                case ScoreboardType.BaseballGame:
                    const setGameScoreboard = event.scoreboard.getScore<SetGameScore>();
                    if (setGameScoreboard) {
                        liveAlert = new LiveAlertEvent(
                            Number(setGameScoreboard.participant1.points),
                            Number(setGameScoreboard.participant2.points),
                            event.id,
                            event.sport.id,
                            event.participants,
                            period,
                            event.scoreboard.time,
                            setGameScoreboard.currentlyServing,
                        );
                    }
                    break;
            }
        }

        return liveAlert;
    }

    private isDuplicateAlert(eventId: string, message: Message): boolean {
        const duplicateAlert = (alert: LiveAlertMessage) =>
            alert.eventId === eventId &&
            (alert.order === message.order || (alert.text === message.text && message.type === this.endOfQuarterOrPeriodId));

        return this.alerts.some(duplicateAlert) || this.queuedAlerts.some(duplicateAlert);
    }

    private isCancelledTouchDown(oldScore?: number, newScore?: number) {
        return oldScore !== undefined && newScore !== undefined && oldScore - newScore === TouchDownInfo.TouchDownPointer;
    }
}
